/**
 * ============================================================================
 * USE MEDIA PERMISSIONS HOOK
 * ============================================================================
 *
 * Tracks browser permission state for microphone and camera, and detects
 * active media streams by wrapping navigator.mediaDevices.getUserMedia.
 *
 * Permission states:
 *   - "prompt" | "denied" → not granted
 *   - "granted" → granted but not in use
 *   - "granted" + active stream → in use
 */

import { useCallback, useEffect, useState } from "react";

export type MediaPermissionState = "granted" | "denied" | "prompt";

export interface MediaPermissions {
  micPermission: MediaPermissionState;
  camPermission: MediaPermissionState;
  micActive: boolean;
  camActive: boolean;
}

/** Singleton tracking of active streams so the wrapper works across hook instances */
const activeStreams = new Set<MediaStream>();
const streamListeners = new Set<() => void>();
let isPatched = false;

function notifyStreamListeners() {
  streamListeners.forEach((fn) => fn());
}

function patchGetUserMedia() {
  if (isPatched || typeof navigator === "undefined" || !navigator.mediaDevices)
    return;
  isPatched = true;

  const original = navigator.mediaDevices.getUserMedia.bind(
    navigator.mediaDevices
  );

  navigator.mediaDevices.getUserMedia = async (
    constraints?: MediaStreamConstraints
  ): Promise<MediaStream> => {
    const stream = await original(constraints);
    activeStreams.add(stream);
    notifyStreamListeners();

    stream.getTracks().forEach((track) => {
      track.addEventListener("ended", () => {
        if (stream.getTracks().every((t) => t.readyState === "ended")) {
          activeStreams.delete(stream);
          notifyStreamListeners();
        }
      });
    });

    return stream;
  };
}

function getActiveMedia(): { micActive: boolean; camActive: boolean } {
  let micActive = false;
  let camActive = false;

  activeStreams.forEach((stream) => {
    stream.getTracks().forEach((track) => {
      if (track.readyState !== "live") return;
      if (track.kind === "audio") micActive = true;
      if (track.kind === "video") camActive = true;
    });
  });

  return { micActive, camActive };
}

export function useMediaPermissions(): MediaPermissions {
  const [micPermission, setMicPermission] =
    useState<MediaPermissionState>("prompt");
  const [camPermission, setCamPermission] =
    useState<MediaPermissionState>("prompt");
  const [micActive, setMicActive] = useState(false);
  const [camActive, setCamActive] = useState(false);

  // Patch getUserMedia once
  useEffect(() => {
    patchGetUserMedia();
  }, []);

  // Query permission states and listen for changes
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.permissions) return;

    let micStatus: PermissionStatus | null = null;
    let camStatus: PermissionStatus | null = null;

    const onMicChange = () => {
      if (micStatus) setMicPermission(micStatus.state as MediaPermissionState);
    };
    const onCamChange = () => {
      if (camStatus) setCamPermission(camStatus.state as MediaPermissionState);
    };

    navigator.permissions
      .query({ name: "microphone" as PermissionName })
      .then((status) => {
        micStatus = status;
        setMicPermission(status.state as MediaPermissionState);
        status.addEventListener("change", onMicChange);
      })
      .catch(() => {
        // Browser doesn't support microphone permission query
      });

    navigator.permissions
      .query({ name: "camera" as PermissionName })
      .then((status) => {
        camStatus = status;
        setCamPermission(status.state as MediaPermissionState);
        status.addEventListener("change", onCamChange);
      })
      .catch(() => {
        // Browser doesn't support camera permission query
      });

    return () => {
      micStatus?.removeEventListener("change", onMicChange);
      camStatus?.removeEventListener("change", onCamChange);
    };
  }, []);

  // Subscribe to active stream changes
  const updateActiveState = useCallback(() => {
    const { micActive: mic, camActive: cam } = getActiveMedia();
    setMicActive(mic);
    setCamActive(cam);
  }, []);

  useEffect(() => {
    streamListeners.add(updateActiveState);
    // Check initial state
    updateActiveState();

    return () => {
      streamListeners.delete(updateActiveState);
    };
  }, [updateActiveState]);

  // Periodically check for tracks that ended without firing the event
  useEffect(() => {
    const interval = setInterval(() => {
      // Clean up dead streams
      let changed = false;
      activeStreams.forEach((stream) => {
        if (stream.getTracks().every((t) => t.readyState === "ended")) {
          activeStreams.delete(stream);
          changed = true;
        }
      });
      if (changed) notifyStreamListeners();
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return { micPermission, camPermission, micActive, camActive };
}
